/**
 * Metric ID Constants - Frontend
 *
 * Centralized constants for all metric IDs used across the analytics frontend.
 * This prevents typos and provides better maintainability.
 *
 * @author Analytics Team
 * @see Jeremy's feedback: "There are a lot (total-leads, ehr-churn-count). I'd move them into enums or an object."
 */

// Acquisition & Lead Metrics
export const ACQUISITION_METRICS = {
  TOTAL_LEADS: 'total-leads',
  CAMPAIGN_MEMBERS_NEW_LEADS: 'campaign-members-new-leads',
  TOTAL_ESTIMATED_LEADS: 'total-estimated-leads',
  ESSENTIALS_TRIALS: 'essentials-trials',
  EHR_DEMOS: 'ehr-demos',
  LEADS_TO_TRIALS_CONVERSION: 'leads-to-trials-conversion',
  LEADS_TO_DEMOS_CONVERSION: 'leads-to-demos-conversion',
  ESSENTIALS_CLOSE_RATE: 'essentials-close-rate',
  EHR_CLOSE_RATE: 'ehr-close-rate',
} as const;

// Subscriber Metrics
export const SUBSCRIBER_METRICS = {
  ESSENTIALS_TOTAL: 'essentials-total-subscribers',
  ESSENTIALS_SEMINAR: 'essentials-seminar-subscribers',
  ESSENTIALS_ORGANIC: 'essentials-organic-subscribers',
  EHR_TOTAL: 'ehr-total-subscribers',
  CURRENT_ACTIVE_ESSENTIALS: 'current-active-essentials',
  CURRENT_ACTIVE_EHR: 'current-active-ehr',
} as const;

// Churn Metrics
export const CHURN_METRICS = {
  ESSENTIALS_COUNT: 'essentials-churn-count',
  ESSENTIALS_RATE: 'essentials-churn-rate',
  EHR_COUNT: 'ehr-churn-count',
  EHR_RATE: 'ehr-churn-rate',
  EHR_DOWNGRADE_COUNT: 'ehr-downgrade-count',
  EHR_DOWNGRADE_RATE: 'ehr-downgrade-rate',
} as const;

// Health Score Metrics
export const HEALTH_METRICS = {
  ESSENTIALS_ONBOARDING: 'essentials-health-onboarding',
  ESSENTIALS_POST_BOARDING: 'essentials-health-post-boarding',
  EHR_SCORE: 'ehr-health-score',
} as const;

// Support Metrics
export const SUPPORT_METRICS = {
  CSAT_SUBMISSIONS: 'support-csat-submissions',
  CSAT_AVG_SCORE: 'support-csat-avg-score',
  CSAT_COMMENTS: 'support-csat-comments',
  CSAT_DETAILS: 'support-csat-details',
  CSAT_AVG_SCORE_LEADERBOARD: 'support-csat-avg-score-leaderboard',
  TOTAL_TICKETS: 'support-total-tickets',
  TOTAL_TICKETS_ALL_TIME: 'support-total-tickets-all-time',
  ESSENTIALS_CASES: 'support-essentials-cases',
  EHR_CASES: 'support-ehr-cases',
  CASE_ORIGIN_BREAKDOWN: 'support-case-origin-breakdown',
  CASE_PRODUCT_BREAKDOWN: 'support-case-product-breakdown',
  CASE_TYPE_BREAKDOWN: 'support-case-type-breakdown',
  CASE_TYPE_BY_REP_BREAKDOWN: 'support-case-type-by-rep-breakdown',
  FEATURE_AREA_CLASSIFICATION_BREAKDOWN: 'support-feature-area-classification-breakdown',
  SENTIMENT_CLASSIFICATION_BREAKDOWN: 'support-sentiment-classification-breakdown',
  AVG_FIRST_RESPONSE_TIME: 'support-avg-first-response-time',
  AVG_FIRST_RESPONSE_TIME_BH: 'support-avg-first-response-time-bh',
  AVG_RESOLUTION_TIME: 'support-avg-resolution-time',
  AVG_RESOLUTION_TIME_BH: 'support-avg-resolution-time-bh',
  CASES_ESCALATED_DEV: 'support-cases-escalated-dev',
  DEV_ESCALATION_RATE: 'support-dev-escalation-rate',
  PRODUCT_REQUESTS_SUBMITTED: 'support-product-requests-submitted',
  AVG_PRODUCT_REQUEST_TIME: 'support-avg-product-request-time',
  FIRST_TOUCH_RESOLUTION: 'support-first-touch-resolution',
  FIRST_TOUCH_RESOLUTION_RATE: 'support-first-touch-resolution-rate',
  CASES_CLOSED_IN_TIMEFRAME: 'support-cases-closed-in-timeframe',
  CASES_CLOSED_BY_SUBSCRIBERS: 'support-cases-closed-by-subscribers',
  SUBSCRIBER_CLOSED_RATE: 'support-subscriber-closed-rate',
  TOTAL_TICKETS_LEADERBOARD: 'support-total-tickets-leaderboard',
  ALL_TIME_TICKETS_LEADERBOARD: 'support-all-time-tickets-leaderboard',
  AVG_FIRST_RESPONSE_TIME_LEADERBOARD: 'support-avg-first-response-time-leaderboard',
  AVG_FIRST_RESPONSE_TIME_BH_LEADERBOARD: 'support-avg-first-response-time-bh-leaderboard',
  AVG_RESOLUTION_TIME_LEADERBOARD: 'support-avg-resolution-time-leaderboard',
  AVG_RESOLUTION_TIME_BH_LEADERBOARD: 'support-avg-resolution-time-bh-leaderboard',
  CASES_ESCALATED_DEV_LEADERBOARD: 'support-cases-escalated-dev-leaderboard',
  DEV_ESCALATION_RATE_LEADERBOARD: 'support-dev-escalation-rate-leaderboard',
  FIRST_TOUCH_RESOLUTION_LEADERBOARD: 'support-first-touch-resolution-leaderboard',
  FIRST_TOUCH_RESOLUTION_RATE_LEADERBOARD: 'support-first-touch-resolution-rate-leaderboard',
  FIRST_RESPONSE_TIME_DISTRIBUTION: 'support-first-response-time-distribution',
  RESOLUTION_TIME_DISTRIBUTION: 'support-resolution-time-distribution',
  ESSENTIALS_TICKETS: 'essentials-support-tickets',
  ESSENTIALS_FIRST_RESPONSE: 'essentials-avg-first-response',
  ESSENTIALS_RESOLUTION: 'essentials-avg-resolution',
  EHR_TICKETS: 'ehr-support-tickets',
  EHR_FIRST_RESPONSE: 'ehr-avg-first-response',
  EHR_RESOLUTION: 'ehr-avg-resolution',
} as const;

// Tech Team Metrics
export const TECH_TEAM_METRICS = {
  TECH_TEAM_CASES: 'tech-team-cases',
  AVG_ASSIGNMENT_TIME: 'tech-team-avg-assignment-time',
  AVG_ASSIGNMENT_TIME_BH: 'tech-team-avg-assignment-time-bh',
  AVG_RESPONSE_TIME: 'tech-team-avg-response-time',
  AVG_RESPONSE_TIME_BH: 'tech-team-avg-response-time-bh',
  AVG_DEV_SUBMISSION_TIME: 'tech-team-avg-dev-submission-time',
  AVG_DEV_SUBMISSION_TIME_BH: 'tech-team-avg-dev-submission-time-bh',
  ISSUES_SUBMITTED_DEV: 'tech-team-issues-submitted-dev',
  AVG_DEV_RESOLUTION_TIME: 'tech-team-avg-dev-resolution-time',
  AVG_DEV_RESOLUTION_TIME_BH: 'tech-team-avg-dev-resolution-time-bh',
  ISSUES_RESOLVED_DEV: 'tech-team-issues-resolved-dev',
  AVG_DEV_FOLLOWUP_TIME: 'tech-team-avg-dev-followup-time',
  AVG_DEV_FOLLOWUP_TIME_BH: 'tech-team-avg-dev-followup-time-bh',
} as const;

// NPS Metrics from Chameleon
export const NPS_METRICS = {
  ESSENTIALS_NPS_SCORE: 'essentials-nps-score',
  EHR_NPS_SCORE: 'ehr-nps-score',
} as const;

// Combined metrics object for easy access
const ALL_METRICS = {
  ...ACQUISITION_METRICS,
  ...SUBSCRIBER_METRICS,
  ...CHURN_METRICS,
  ...HEALTH_METRICS,
  ...SUPPORT_METRICS,
  ...NPS_METRICS,
  ...TECH_TEAM_METRICS,
} as const;

// Section groupings for dashboard organization (lowercase keys to match activeSection)
export const SECTION_METRICS = {
  acquisition: [
    ACQUISITION_METRICS.TOTAL_LEADS,
    ACQUISITION_METRICS.TOTAL_ESTIMATED_LEADS,
    ACQUISITION_METRICS.ESSENTIALS_TRIALS,
    ACQUISITION_METRICS.EHR_DEMOS,
    SUBSCRIBER_METRICS.ESSENTIALS_TOTAL,
    SUBSCRIBER_METRICS.ESSENTIALS_SEMINAR,
    SUBSCRIBER_METRICS.ESSENTIALS_ORGANIC,
    SUBSCRIBER_METRICS.EHR_TOTAL,
    ACQUISITION_METRICS.LEADS_TO_TRIALS_CONVERSION,
    ACQUISITION_METRICS.LEADS_TO_DEMOS_CONVERSION,
    ACQUISITION_METRICS.ESSENTIALS_CLOSE_RATE,
    ACQUISITION_METRICS.EHR_CLOSE_RATE,
  ],
  essentials: [
    CHURN_METRICS.ESSENTIALS_COUNT,
    CHURN_METRICS.ESSENTIALS_RATE,
    HEALTH_METRICS.ESSENTIALS_ONBOARDING,
    HEALTH_METRICS.ESSENTIALS_POST_BOARDING,
    NPS_METRICS.ESSENTIALS_NPS_SCORE,
    SUPPORT_METRICS.ESSENTIALS_TICKETS,
    SUPPORT_METRICS.ESSENTIALS_FIRST_RESPONSE,
    SUPPORT_METRICS.ESSENTIALS_RESOLUTION,
  ],
  ehr: [
    CHURN_METRICS.EHR_COUNT,
    CHURN_METRICS.EHR_RATE,
    CHURN_METRICS.EHR_DOWNGRADE_COUNT,
    CHURN_METRICS.EHR_DOWNGRADE_RATE,
    HEALTH_METRICS.EHR_SCORE,
    NPS_METRICS.EHR_NPS_SCORE,
    SUPPORT_METRICS.EHR_TICKETS,
    SUPPORT_METRICS.EHR_FIRST_RESPONSE,
    SUPPORT_METRICS.EHR_RESOLUTION,
  ],
} as const;

// Type definitions for better TypeScript support
type AcquisitionMetricId = (typeof ACQUISITION_METRICS)[keyof typeof ACQUISITION_METRICS];
type SubscriberMetricId = (typeof SUBSCRIBER_METRICS)[keyof typeof SUBSCRIBER_METRICS];
type ChurnMetricId = (typeof CHURN_METRICS)[keyof typeof CHURN_METRICS];
type HealthMetricId = (typeof HEALTH_METRICS)[keyof typeof HEALTH_METRICS];
type SupportMetricId = (typeof SUPPORT_METRICS)[keyof typeof SUPPORT_METRICS];
type NpsMetricId = (typeof NPS_METRICS)[keyof typeof NPS_METRICS];
type MetricId = (typeof ALL_METRICS)[keyof typeof ALL_METRICS];
